import { normalizeCode } from '../shared/code'
import { deriveClientKey } from '../shared/passphrase'
import type {
  HouseholdPlan,
  HouseholdSettings,
  InviteInfo,
  Locale,
  Role,
  Snapshot,
} from '../shared/types'
import { getDict, getLocale } from './i18n'

const TOKEN_KEY = 'moonly.token'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* 隐私模式下 localStorage 可能不可用，忽略即可 */
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // 服务端的报错是直接显示给用户的，得跟界面同一门语言。
        // 用自定义头而不是 Accept-Language：用户在设置里选的语言可能
        // 和浏览器语言不一样，以他自己选的为准。
        'X-Moonly-Locale': getLocale(),
        ...init.headers,
      },
    })
  } catch {
    throw new ApiError(getDict().common.networkError, 0)
  }

  if (res.status === 401) {
    // token 过期或口令改过了，清掉本地状态让用户重新登录
    setToken(null)
    throw new ApiError(getDict().common.sessionExpired, 401)
  }

  const text = await res.text()

  // 服务端崩溃时 Cloudflare 会回一段纯文本 'Internal Server Error'。
  // 不能无脑 JSON.parse —— 那样用户看到的会是「Unexpected token 'I'」这种
  // 毫无意义的解析错误，真正的问题反而被盖住了。
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      throw new ApiError(
        res.ok ? getDict().common.unexpectedResponse : getDict().common.serverError(res.status),
        res.status,
      )
    }
  }

  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : getDict().common.genericError(res.status)
    throw new ApiError(msg, res.status)
  }
  return data as T
}

/**
 * 登录。口令在本机拉伸成 clientKey 后才发出去，明文不出这台设备。
 * 这一步要花几十到几百毫秒，界面上会显示「正在验证」。
 */
export async function login(name: string, passphrase: string): Promise<Snapshot['user']> {
  const clientKey = await deriveClientKey(name, passphrase)
  const res = await request<{ token: string; user: Snapshot['user'] }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ name, clientKey }),
  })
  setToken(res.token)
  return res.user
}

export interface SignupInput {
  loginName: string
  displayName: string
  email: string
  passphrase: string
  householdName: string
  /** 注册的人自己是记录周期的一方，还是伴侣 */
  role: Role
}

/** 注册一个新家庭。和登录一样，口令在本机拉伸后才发出去。 */
export async function signup(input: SignupInput): Promise<Snapshot['user']> {
  const clientKey = await deriveClientKey(input.loginName, input.passphrase)
  const res = await request<{ token: string; user: Snapshot['user'] }>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify({
      loginName: input.loginName,
      displayName: input.displayName,
      email: input.email,
      householdName: input.householdName,
      role: input.role,
      // 浏览器知道自己在哪个时区，不必让用户在注册页选一次
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // 存进账号：以后发给这个人的邮件按它来
      locale: getLocale(),
      clientKey,
    }),
  })
  setToken(res.token)
  return res.user
}

export interface InvitePreview {
  householdName: string
  inviterName: string
  role: Role
}

/** 拿邀请码看一眼是谁在邀请。不需要登录。 */
export function peekInvite(code: string): Promise<InvitePreview> {
  return request<InvitePreview>(`/auth/invite/${encodeURIComponent(normalizeCode(code))}`)
}

/** 加入已有家庭时不用填家庭名和角色 —— 邀请码里都定好了。 */
export type JoinInput = Omit<SignupInput, 'householdName' | 'role'>

export async function join(code: string, input: JoinInput): Promise<Snapshot['user']> {
  const clientKey = await deriveClientKey(input.loginName, input.passphrase)
  const res = await request<{ token: string; user: Snapshot['user'] }>('/auth/join', {
    method: 'POST',
    body: JSON.stringify({
      code: normalizeCode(code),
      loginName: input.loginName,
      displayName: input.displayName,
      email: input.email,
      locale: getLocale(),
      clientKey,
    }),
  })
  setToken(res.token)
  return res.user
}

/** 服务端能力探测。目前只用来决定要不要显示「忘记口令了？」。 */
export function fetchConfig(): Promise<{ emailRecovery: boolean }> {
  return request('/config')
}

// ── 找回口令 ────────────────────────────────────────────────────────────

/**
 * 申请重置链接。
 * 服务端不管邮箱存不存在都回同样的结果（避免被拿来枚举用户），
 * 所以这里也没什么可判断的 —— 界面只说「如果这个邮箱注册过，信已经发了」。
 */
export function forgotPassword(email: string): Promise<{ ok: true }> {
  return request('/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) })
}

/** 用重置 token 换回登录名 —— 派生口令要拿它当盐，不知道就算不对。 */
export function peekReset(token: string): Promise<{ loginName: string; displayName: string }> {
  return request(`/auth/reset/${encodeURIComponent(token)}`)
}

export async function resetPassword(
  token: string,
  loginName: string,
  passphrase: string,
): Promise<Snapshot['user']> {
  const clientKey = await deriveClientKey(loginName, passphrase)
  const res = await request<{ token: string; user: Snapshot['user'] }>('/auth/reset', {
    method: 'POST',
    body: JSON.stringify({ token, clientKey }),
  })
  // 重置成功直接给一个新 session，省得用户刚设完口令又要立刻登一次
  setToken(res.token)
  return res.user
}

export function fetchSnapshot(): Promise<Snapshot> {
  return request<Snapshot>('/snapshot')
}

// ── 席位、邀请、兑换 ────────────────────────────────────────────────────

export function fetchInvite(): Promise<{ plan: HouseholdPlan; invite: InviteInfo | null }> {
  return request('/invite')
}

export function createInvite(): Promise<{ invite: InviteInfo }> {
  return request('/invite', { method: 'POST' })
}

export function revokeInvite(): Promise<{ ok: true }> {
  return request('/invite', { method: 'DELETE' })
}

export function redeem(code: string): Promise<{ plan: HouseholdPlan }> {
  return request('/redeem', {
    method: 'POST',
    body: JSON.stringify({ code: normalizeCode(code) }),
  })
}

/**
 * 改（或清空）邮箱。必须带当前口令 —— 邮箱是找回口令的入口，
 * 不验口令的话，摸到一台没锁屏的手机就能把账号接管走。
 * 传空字符串表示清空。
 */
export async function changeEmail(
  loginName: string,
  email: string,
  currentPassphrase: string,
): Promise<{ email: string | null }> {
  const currentKey = await deriveClientKey(loginName, currentPassphrase)
  return request('/account/email', {
    method: 'PUT',
    body: JSON.stringify({ email, currentKey }),
  })
}

/**
 * 把界面语言记到账号上。
 *
 * 界面本身不等这个请求 —— 语言存在设备上，点一下立刻就换了。这一趟是为了
 * 让找回口令的邮件、以及以后换设备登录时的默认值跟得上。失败了也不影响使用。
 */
export function setLocale(locale: Locale): Promise<{ locale: Locale }> {
  return request('/account/locale', {
    method: 'PUT',
    body: JSON.stringify({ locale }),
  })
}

/** 注销。confirm 必须是自己的登录名，服务端会再核一遍。 */
export function deleteAccount(confirm: string): Promise<{ householdDeleted: boolean }> {
  return request('/account/delete', {
    method: 'POST',
    body: JSON.stringify({ confirm }),
  })
}

export interface DayPayload {
  period?: { flow: string; note?: string | null } | null
  symptom?: {
    symptoms: string[]
    mood: number | null
    energy: number | null
    note: string | null
  } | null
  intimacy?: { protection: string; note?: string | null } | null
}

/** 保存某一天的记录。返回更新后的完整快照，前端不用自己维护增量状态。 */
export function saveDay(date: string, payload: DayPayload): Promise<Snapshot> {
  return request<Snapshot>(`/day/${date}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export function saveSettings(
  s: Partial<HouseholdSettings>,
): Promise<{ settings: HouseholdSettings; today: string }> {
  return request('/settings', { method: 'PUT', body: JSON.stringify(s) })
}

export async function changePassword(
  loginName: string,
  current: string,
  next: string,
): Promise<void> {
  const [currentKey, nextKey] = await Promise.all([
    deriveClientKey(loginName, current),
    deriveClientKey(loginName, next),
  ])
  const res = await request<{ token: string }>('/auth/password', {
    method: 'POST',
    body: JSON.stringify({ currentKey, nextKey }),
  })
  // 改口令会作废所有旧 token，包括当前这个，所以换上服务端刚签发的新 token
  setToken(res.token)
}

/** 导出需要带 token，所以先取回内容再触发下载，不能直接开新窗口 */
export async function exportData(): Promise<void> {
  const token = getToken()
  const res = await fetch('/api/export', {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Moonly-Locale': getLocale(),
    },
  })
  if (!res.ok) throw new ApiError(getDict().common.exportFailed, res.status)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `moonly-${new Date().toISOString().slice(0, 10)}.json`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
